import React, { useMemo } from 'react';
import { Table } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import CustomTooltip from "../Components/Modals/CustomTooltip";
import EmptyCard from "../EmptyCard";
import { variantTokens, primaryShades } from "../../theme/theme";
import { formatNumberBritish, formatPercentageBritish } from "../../utils/numberFormatting";
import LoadingCard from '../Components/Cards/LoadingCard';

const PagesAnalytics = ({ data, loading, clearFiltersVisible, onResetFilters, calculationFunction }) => {

    // Utilizziamo la funzione di calcolo che viene passata dalla pagina principale
    const tableData = useMemo(() => {
        // Se è disponibile una funzione di calcolo esterna, la utilizziamo
        if (calculationFunction && typeof calculationFunction === 'function') {
            try {
                const calculatedData = calculationFunction(data);

                // Trasformiamo i dati nel formato atteso dalla tabella
                return calculatedData.map((item, index) => ({
                    key: `${item.page}-${index}`,
                    pageName: item.page,
                    visualisations: item.visits,
                    viewingPercentage: item.viewingPercentage,
                    viewingTotalTime: item.viewingTotalTime,
                    viewingAvgTime: item.viewingAvgTime
                }));
            } catch (error) {
                console.error("Error using the calculation function:", error);
                // In case of error, use the original logic
            }
        }

        // Logica originale se non c'è una funzione di calcolo o si è verificato un errore
        return processAnalyticsData(data);
    }, [data, calculationFunction]);

    const processAnalyticsData = (dataCollection) => {
        if (!dataCollection || dataCollection.length === 0) return [];

        const combinedAnalytics = dataCollection.flatMap((item) => item.page_analitycs_list || []);

        const totalVisualisations = combinedAnalytics.length;

        const pageMap = combinedAnalytics.reduce((acc, page) => {
            let { page_name, visualizazion_time = 0 } = page;

            if (page_name.startsWith('VTO -')) {
                page_name = 'VTO';
            } else if (page_name.startsWith('SingleGlassesPage -')) {
                page_name = 'SingleGlassesPage';
            } else if (page_name.startsWith('Product Page - ')) {
                page_name = 'Product Page';
            }

            if (!acc[page_name]) {
                acc[page_name] = { pageName: page_name, visualizazion_time: 0, visualisations: 0 };
            }
            acc[page_name].visualizazion_time += visualizazion_time;
            acc[page_name].visualisations += 1;

            return acc;
        }, {});

        return Object.values(pageMap)
            .map((page, index) => {
                const { pageName, visualizazion_time, visualisations } = page;
                const viewingAvgTime = Math.floor(visualizazion_time / visualisations);

                return {
                    key: `${pageName}-${index}`,
                    pageName,
                    visualisations,
                    viewingPercentage: totalVisualisations > 0
                        ? ((visualisations / totalVisualisations) * 100).toFixed(2)
                        : 0,
                    viewingTotalTime: formatTime(visualizazion_time),
                    viewingAvgTime: formatTime(viewingAvgTime),
                };
            })
            .sort((a, b) => b.visualisations - a.visualisations); // Ordina per Num of Visualisations in modo decrescente
    };

    const formatTime = (seconds) => {
        const hrs = Math.floor(seconds / 3600);
        const mins = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;
        return `${String(hrs).padStart(2, '0')}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    };

    // Verifica se ci sono dati da visualizzare
    const hasData = tableData && tableData.length > 0;

    const columns = [
        {
            title: (
                <span>
                    Page{' '}
                    <CustomTooltip title="The name of the page being analyzed.">
                        <InfoCircleOutlined />
                    </CustomTooltip>
                </span>
            ),
            dataIndex: 'pageName',
            key: 'pageName',
            sorter: (a, b) => a.pageName.localeCompare(b.pageName),
            showSorterTooltip: false,
            width: '25%',
        },
        {
            title: (
                <span>
                    Views{' '}
                    <CustomTooltip title="The total number of visualisations for the page.">
                        <InfoCircleOutlined />
                    </CustomTooltip>
                </span>
            ),
            dataIndex: 'visualisations',
            key: 'visualisations',
            sorter: (a, b) => a.visualisations - b.visualisations,
            defaultSortOrder: 'descend', // Ordinamento predefinito decrescente
            showSorterTooltip: false,
            width: '15%',
            render: (value) => formatNumberBritish(value, 0),
        },
        {
            title: (
                <span>
                    %{' '}
                    <CustomTooltip title="The percentage of visualisations relative to the total.">
                        <InfoCircleOutlined />
                    </CustomTooltip>
                </span>
            ),
            dataIndex: 'viewingPercentage',
            key: 'viewingPercentage',
            sorter: (a, b) => a.viewingPercentage - b.viewingPercentage,
            render: (value) => formatPercentageBritish(value),
            showSorterTooltip: false,
            width: '15%',
        },
        {
            title: (
                <span>
                    Tot Time{' '}
                    <CustomTooltip title="The total time spent on the page in hh:mm:ss format.">
                        <InfoCircleOutlined />
                    </CustomTooltip>
                </span>
            ),
            dataIndex: 'viewingTotalTime',
            key: 'viewingTotalTime',
            sorter: (a, b) => timeInSeconds(a.viewingTotalTime) - timeInSeconds(b.viewingTotalTime),
            showSorterTooltip: false,
            width: '15%',
        },
        {
            title: (
                <span>
                    Avg Time{' '}
                    <CustomTooltip title="The average time spent per visualisation in hh:mm:ss format.">
                        <InfoCircleOutlined />
                    </CustomTooltip>
                </span>
            ),
            dataIndex: 'viewingAvgTime',
            key: 'viewingAvgTime',
            sorter: (a, b) => timeInSeconds(a.viewingAvgTime) - timeInSeconds(b.viewingAvgTime),
            showSorterTooltip: false,
            width: '15%',
        },
    ];

    const timeInSeconds = (timeString) => {
        const [hrs, mins, secs] = timeString.split(':').map(Number);
        return hrs * 3600 + mins * 60 + secs;
    };

    // Renderizza EmptyCard quando non ci sono dati
    if (!loading && !hasData) {
        return <EmptyCard />;
    }

    return (
        <div style={{ width: "100%" }}>
            {loading ? (
                <LoadingCard />
            ) : (
                <div className="table-container">
                    <Table
                        dataSource={tableData}
                        rowKey="key"
                        columns={columns}
                        pagination={{
                            defaultPageSize: 10,
                            showSizeChanger: true,
                            pageSizeOptions: ['10', '20', '50', '100', '200', '500', '1000'],
                        }}
                        size="small"
                        scroll={{ x: "max-content" }}
                        className="analytics-table"
                    />
                </div>
            )}
            <style jsx="true">{`
                .table-container {
                    border: 1px solid #d9d9d9;
                    border-radius: 8px;
                    overflow: hidden;
                }
                .analytics-table .ant-pagination-item {
                    background: transparent;
                    border: none;
                }
                .analytics-table .ant-pagination-item-active {
                    background: ${primaryShades.primary300};
                }
                .analytics-table .ant-pagination-item-active a {
                    color: ${variantTokens.white};
                }
                .analytics-table .ant-pagination {
                    padding-right: 16px;
                }
                .analytics-table th.ant-table-cell {
                    text-align: left;
                }
                .analytics-table td.ant-table-cell {
                    text-align: left;
                }
                .analytics-table th.ant-table-cell[aria-sort]:not([aria-sort="none"]) {
                    background: ${primaryShades.primary50};
                }
                .analytics-table .ant-table-column-sorter-up.active,
                .analytics-table .ant-table-column-sorter-down.active {
                    color: ${primaryShades.primary500};
                }
            `}</style>
        </div>
    );
};

export default PagesAnalytics;